import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { cn } from 'src/lib/utils'
import { fetchCoinInfo, selectCoin } from '../../core/reducers/coinInfo'
import { currencyFullValue, percentageFormat, priceColor } from '../../core/helpers'
import { ReactComponent as PriceUp } from './price-up.svg'
import { ReactComponent as PriceDown } from './price-down.svg'
import { Card, CardContent } from '../ui/card'
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'  // Temporarily disabled due to React 17 compatibility
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'

import Chart from '../Chart/Chart'
import CoinMarkets from './CoinMarkets'
import CoinPerformance from './CoinPerformance'
import CoinStats from './CoinStats'
import CoinInfo from './CoinInfo'
import CoinVolume from './CoinVolume'
import CoinSidebar from './CoinSidebar'
import CoinCategories from './CoinCategories'
import CoinSecurity from './CoinSecurity'

function Coin({ match }) {
  const coinId = match.params.id
  const coin = useSelector(state => selectCoin(state, coinId))
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchCoinInfo(coinId))
  }, [dispatch, coinId])

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Coin Header */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                {coin.fetching ? (
                  <>
                    <Skeleton className="h-20 w-20 rounded-full" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-6 w-32" />
                      <Skeleton className="h-8 w-48" />
                    </div>
                  </>
                ) : (
                  <>
                    <img
                      className="w-20 h-20 rounded-full object-contain"
                      src={coin.image}
                      alt={coin.name}
                    />
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-walme-text mb-2">{coin.name}</h1>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-3xl font-bold text-walme-text uppercase">{coin.symbol}</span>
                        <Badge variant="secondary">Rank #{coin.rank}</Badge>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <CoinCategories categories={coin.categories} />

              <div className="border-t border-walme-border my-6" />

              <div className="flex items-center gap-4 flex-wrap">
                {coin.fetching ? (
                  <>
                    <Skeleton className="h-12 w-48" />
                    <Skeleton className="h-10 w-32" />
                  </>
                ) : (
                  <>
                    <div className="text-4xl font-bold text-walme-text">
                      {currencyFullValue(coin.price)}
                    </div>
                    <PriceChange price={coin.priceChange24} />
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Chart */}
          <Card>
            <CardContent className="p-6">
              <Chart key={coinId} coin={coin.symbol} coinId={coinId} />
            </CardContent>
          </Card>

          {/* Coin Content - All sections displayed */}
          <div className="space-y-6">
            <CoinPerformance
              performance={coin.performance}
              priceRanges={coin.priceRanges}
              isFetching={coin.fetching}
            />
            <CoinVolume
              volumes={coin.volumes}
              symbol={coin.symbol}
              coinId={coinId}
              launchDate={coin.launchDate}
            />
            <CoinMarkets markets={coin.markets} />
            <CoinSecurity />
            <CoinStats funds={coin.funds} holders={[]} />
            <CoinInfo description={coin.description} guide={coin.guide} whitepaper={coin.whitepaper} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <CoinSidebar coin={coin.id} links={coin.links} platforms={coin.platforms} />
          </div>
        </div>
      </div>
    </div>
  )
}

function PriceChange({ className, price }) {
  return (
    <div className={cn('flex items-center gap-2 text-xl font-semibold', className, priceColor(price))}>
      {price > 0 ? <PriceUp className="w-5 h-5" /> : <PriceDown className="w-5 h-5" />}
      <span>{percentageFormat(Math.abs(price), { forceSign: false })}</span>
    </div>
  )
}

export default Coin
